import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import uniAdminAuthService from "../../services/university-admin/authService";
import { useNotification } from "../../context/NotificationContext";

const UMForgotPassword = () => {
  const { t, i18n } = useTranslation(["auth", "validation"]);
  const { showSuccess } = useNotification();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const toggleLanguage = () => {
    const nextLang = i18n.language.startsWith("en") ? "tr" : "en";
    i18n.changeLanguage(nextLang);
    window.localStorage.setItem("i18nextLng", nextLang);
  };

  const validate = () => {
    if (!email.trim()) {
      setError(t("validation:required.email"));
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError(t("validation:invalid.email"));
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const result = await uniAdminAuthService.forgottenPassword(email.trim());
      if (result.success) {
        showSuccess(t("auth:forgotPassword.otpSent"));
        navigate("/uni-admin/reset-password", { state: { email: email.trim() } });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light p-3 p-md-4 p-lg-5">
      <div className="card border-0 shadow-lg w-100 overflow-hidden" style={{ maxWidth: "1100px", borderRadius: "20px" }}>
        <div className="row g-0">
          
          {/* Left Side - Form */}
          <div className="col-12 col-lg-6 bg-white p-4 p-md-5 d-flex flex-column position-relative">
            {/* Language Switcher */}
            <div className="d-flex justify-content-end mb-4">
              <button 
                onClick={toggleLanguage} 
                className="btn btn-sm btn-light rounded-pill px-3 fw-medium border"
                style={{ transition: "all 0.2s" }}
              >
                <i className="fas fa-globe me-2 text-primary"></i>
                {i18n.language.startsWith("en") ? "Türkçe" : "English"}
              </button>
            </div>

            <div className="flex-grow-1 d-flex flex-column justify-content-center w-100 mx-auto" style={{ maxWidth: "400px" }}>
              <div className="mb-5">
                <div className="d-flex align-items-center mb-4">
                  <div className="me-2" style={{ width: "32px", height: "32px" }}>
                    <img src="/favicon/logo192.png" alt="The Uniflow Logo" className="img-fluid rounded" />
                  </div>
                  <span className="fw-bold fs-5 text-dark">The Uniflow</span>
                </div>
                
                <h2 className="fw-bold text-dark mb-2" style={{ fontSize: "2rem", letterSpacing: "-0.5px" }}>
                  {t("auth:forgotPassword.title", "Forgot Password")}
                </h2>
                <p className="text-muted">
                  {t("auth:forgotPassword.subtitle", "Enter your email to receive a reset code.")}
                </p>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <div className={`input-group ${error ? 'is-invalid border border-danger rounded' : ''}`}>
                    <span className={`input-group-text bg-white border-end-0 px-3 ${error ? 'border-0' : 'text-muted'}`}>
                      <i className="far fa-envelope"></i>
                    </span>
                    <input
                      type="email"
                      className={`form-control border-start-0 ps-0 py-2 ${error ? 'border-0 bg-transparent' : ''}`}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError("");
                      }}
                      placeholder={t("forgotPassword.emailPlaceholder", "Email")}
                      autoFocus
                      style={error ? { boxShadow: "none" } : { boxShadow: "none", borderColor: "#dee2e6" }}
                    />
                  </div>
                  {error && <div className="text-danger small mt-2 fw-medium"><i className="fas fa-exclamation-circle me-1"></i>{error}</div>}
                </div>
                
                <button
                  type="submit"
                  className="btn btn-primary w-100 py-2 fw-medium auth-btn rounded-3 mb-4"
                  disabled={loading}
                  style={{ backgroundColor: "#0d6efd", border: "none" }}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      {t("auth:forgotPassword.submitting", "Sending...")}
                    </>
                  ) : (
                    t("auth:forgotPassword.submitButton", "Reset Password")
                  )}
                </button>

                <div className="text-center">
                  <span className="text-muted small me-1">{t("forgotPassword.rememberedPassword", "Remembered your password?")}</span>
                  <Link to="/uni-admin/login" className="text-decoration-none small fw-medium" style={{ color: "#0d6efd" }}>
                    {t("auth:forgotPassword.backToLogin", "Log In")}
                  </Link>
                </div>
              </form>
            </div>
          </div>
          
          {/* Right Side - Blue Banner */}
          <div className="col-12 col-lg-6 d-none d-lg-flex flex-column align-items-center justify-content-center text-white position-relative" style={{ backgroundColor: "#0d6efd", minHeight: "600px" }}>
            {/* Abstract Background Circles */}
            <div className="position-absolute rounded-circle" style={{ width: "500px", height: "500px", border: "60px solid rgba(255,255,255,0.05)", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}></div>
            <div className="position-absolute rounded-circle" style={{ width: "350px", height: "350px", border: "40px solid rgba(255,255,255,0.05)", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}></div>
            
            {/* Center Illustration */}
            <div className="position-relative z-1 mb-5">
              <div className="bg-white rounded-circle shadow-lg d-flex align-items-center justify-content-center mx-auto" style={{ width: "160px", height: "160px" }}>
                <i className="fas fa-shield-alt text-primary" style={{ fontSize: "4rem" }}></i>
              </div>
              
              {/* Floating elements */}
              <div className="position-absolute bg-white rounded-circle shadow d-flex align-items-center justify-content-center float-icon-1" style={{ width: "50px", height: "50px", top: "10%", left: "-20px" }}>
                <i className="fas fa-key text-warning fs-5"></i>
              </div>
              <div className="position-absolute bg-white rounded-circle shadow d-flex align-items-center justify-content-center float-icon-2" style={{ width: "45px", height: "45px", bottom: "10%", right: "-10px" }}>
                <i className="fas fa-lock text-success fs-5"></i>
              </div>
            </div>
            
            <div className="text-center mt-4 px-5 position-relative z-1">
              <h3 className="fw-bold mb-3">{t("marketing.forgotPasswordTitle", "Secure your account.")}</h3>
              <p className="text-white-50 px-4">
                {t("marketing.forgotPasswordSubtitle", "Regain access to your university dashboard quickly and securely.")}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        .input-group-text {
          border-color: #dee2e6;
        }
        .form-control:focus {
          border-color: #dee2e6;
          box-shadow: none;
        }
        .input-group:focus-within {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
          border-radius: 0.375rem;
        }
        .input-group:focus-within .input-group-text,
        .input-group:focus-within .form-control,
        .input-group:focus-within .btn {
          border-color: #0d6efd !important;
        }
        .auth-btn:hover:not(:disabled) {
          background-color: #0b5ed7 !important;
        }
        @keyframes floating {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        .float-icon-1 { animation: floating 3s ease-in-out infinite; }
        .float-icon-2 { animation: floating 4s ease-in-out infinite; animation-delay: 1s; }
      `}</style>
    </div>
  );
};

export default UMForgotPassword;
